import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  loginSuccess,
  logout,
  selectActiveContext,
  selectTokens,
  updateAccessToken,
  useAppDispatch,
  useAppSelector,
} from '@doccareplus/kernel/application';
import { apiClientFactory } from '@doccareplus/api-client';
import { authApi, configureAuthInterceptors } from '@doccareplus/kernel/infrastructure/api/auth.service';

export function AuthSessionBridge() {
  const dispatch = useAppDispatch();
  const tokens = useAppSelector(selectTokens);
  const activeContext = useAppSelector(selectActiveContext);
  const queryClient = useQueryClient();

  // Every other feature's TanStack Query cache (patients, scheduling, billing, ...) otherwise survives a
  // logout/login cycle untouched in memory. Clear it the moment a session ends so a new session (or a
  // different user signing in on the same tab) never serves stale cached data from the previous one.
  // Guard on a ref, not "was authenticated before", so this only fires on a real session→logged-out
  // transition — never on the initial mount of an already-logged-out app.
  const wasAuthenticated = useRef(Boolean(tokens));
  useEffect(() => {
    if (wasAuthenticated.current && !tokens) {
      queryClient.clear();
    }
    wasAuthenticated.current = Boolean(tokens);
  }, [tokens, queryClient]);

  // The persisted active context is the single source of truth for the
  // per-request auth headers (Model B). Re-applied on every reload.
  useEffect(() => {
    if (activeContext) {
      apiClientFactory.addHeader('X-Clinic-Id', activeContext.clinicId);
      apiClientFactory.addHeader('X-Active-Role', activeContext.role);
    } else {
      apiClientFactory.removeHeader('X-Clinic-Id');
      apiClientFactory.removeHeader('X-Active-Role');
    }
  }, [activeContext]);

  useEffect(() => {
    configureAuthInterceptors(
      () => tokens?.accessToken ?? null,
      async () => {
        if (!tokens?.accessToken || !tokens.refreshToken) {
          return null;
        }

        const refreshed = await authApi.refreshToken(tokens.accessToken, tokens.refreshToken);
        dispatch(loginSuccess({ user: refreshed.user, tokens: refreshed.tokens, memberships: refreshed.memberships }));
        dispatch(updateAccessToken(refreshed.tokens.accessToken));
        return refreshed.tokens.accessToken;
      },
      () => {
        dispatch(logout());
      }
    );
  }, [dispatch, tokens]);

  return null;
}
